import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, X, Check, Save, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SafeImage from '../SafeImage';
import useFeatureGating from '../../hooks/useFeatureGating';
import {
  resolveEventStartDateTime,
  resolveEventEndDateTime,
  toLocalInput as _toLocalInput,
} from '../../lib/eventDateTime';

const _START_PRESETS = [
  { label: '−2h', m: 120 }, { label: '−1h', m: 60 }, { label: '−30m', m: 30 }, { label: '−15m', m: 15 },
];
const _END_PRESETS = [
  { label: '+15m', m: 15 }, { label: '+30m', m: 30 }, { label: '+1h', m: 60 }, { label: '+2h', m: 120 },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function BrandingSection({ form, uploadingLogo, uploadingImage, savingOrgLogo, onFileUpload, onSaveOrgLogoDefault }) {
  const { canUse } = useFeatureGating();
  const canUseCustomBranding = canUse('custom_branding');
  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-6 relative" data-testid="branding-section">
      <h2 className="text-xl font-semibold text-[#EAE7E1] mb-2 tracking-tight flex items-center">
        Branding
        {!canUseCustomBranding && (
          <span className="ml-2 text-[9px] uppercase tracking-widest text-[#D4AF37]/80 border border-[#D4AF37]/25 rounded-sm px-1.5 py-0.5" data-testid="branding-premium-label">Premium</span>
        )}
      </h2>
      {!canUseCustomBranding && (
        <div className="mb-4 flex items-start gap-2 text-[11px] text-[#D4AF37] bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm px-3 py-2" data-testid="branding-locked-banner">
          <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span><b>Premium feature — customize your brand.</b> Your event will use VelvetGate&rsquo;s default branding until you upgrade.</span>
        </div>
      )}
      <fieldset disabled={!canUseCustomBranding} className={!canUseCustomBranding ? 'opacity-60' : ''}>
      <div className="space-y-6">
        <div>
          <Label>Organizer Logo</Label>
          <p className="text-xs text-[#777] mt-1 mb-2">Your brand, venue, or organization logo. Shown alongside event details.</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && onFileUpload(e.target.files[0], 'logo')} className="hidden" id="logo-upload" />
            <Button type="button" variant="outline" className="w-full sm:w-auto border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" disabled={uploadingLogo} onClick={() => document.getElementById('logo-upload').click()} data-testid="logo-upload-button"><Upload className="w-4 h-4 mr-2" />{uploadingLogo ? 'Uploading...' : 'Upload'}</Button>
            {form.organizer_logo_url && (
              <>
                <div className="flex items-center gap-2 p-2 border border-[#C6A66B]/40 rounded-sm bg-[#C6A66B]/5 min-w-0" data-testid="organizer-logo-preview-wrap">
                  <SafeImage src={`${BACKEND_URL}${form.organizer_logo_url}`} alt="Organizer Logo" className="h-12 w-20 rounded flex-shrink-0" imgClassName="bg-white p-1 object-contain" fallbackLabel="LOGO" />
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#C6A66B] uppercase tracking-wider"><Check className="w-3 h-3" />Uploaded</span>
                </div>
                <Button type="button" variant="ghost" className="w-full sm:w-auto text-xs text-[#AFAFAF] hover:text-[#D4AF37]" onClick={onSaveOrgLogoDefault} disabled={savingOrgLogo} data-testid="save-org-logo-default">{savingOrgLogo ? 'Saving...' : 'Save as default'}</Button>
              </>
            )}
          </div>
        </div>
        <div>
          <Label>Event Flyer / Event Image</Label>
          <p className="text-xs text-[#777] mt-1 mb-2">Specific to this event. Used as the hero image on the public event page.</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && onFileUpload(e.target.files[0], 'image')} className="hidden" id="image-upload" />
            <Button type="button" variant="outline" className="w-full sm:w-auto border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" disabled={uploadingImage} onClick={() => document.getElementById('image-upload').click()} data-testid="image-upload-button"><Upload className="w-4 h-4 mr-2" />{uploadingImage ? 'Uploading...' : 'Upload'}</Button>
            {form.event_image_url && (
              <div className="flex items-center gap-2 p-2 border border-[#C6A66B]/40 rounded-sm bg-[#C6A66B]/5 min-w-0" data-testid="event-flyer-preview-wrap">
                <SafeImage src={`${BACKEND_URL}${form.event_image_url}`} alt="Event Flyer" className="h-24 w-40 rounded flex-shrink-0" imgClassName="object-cover" fallbackLabel="FLYER" />
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#C6A66B] uppercase tracking-wider"><Check className="w-3 h-3" />Uploaded</span>
              </div>
            )}
          </div>
        </div>
      </div>
      </fieldset>
    </div>
  );
}

export function StaffAccessSection({ form, setForm, staffEmail, setStaffEmail, addingStaff, setAddingStaff, API, onSaveDefault }) {

  const handleAddStaff = async () => {
    setAddingStaff(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/users/default-staff`, { email: staffEmail.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      if (!(form.staff || []).some(s => s.email === res.data.email)) {
        setForm(prev => ({ ...prev, staff: [...(prev.staff || []), res.data] }));
      }
      setStaffEmail('');
      toast.success(`Added ${res.data.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add staff'); }
    finally { setAddingStaff(false); }
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#3E4F66]/30 rounded-lg p-6" data-testid="staff-access-section">
      <h2 className="text-xl font-semibold text-[#EAE7E1] tracking-tight mb-1">Check-In Staff & Access</h2>
      <p className="text-sm text-[#AFAFAF] mb-5">Optional. Add staff and set when they can access check-in.</p>

      <div className="space-y-2 mb-4">
        {(form.staff || []).map((s, i) => (
          <div key={s.user_id || i} className="flex items-center justify-between p-3 bg-[#050505] border border-[#1A1A1A] rounded-sm">
            <div><div className="text-sm font-medium text-[#EAE7E1]">{s.name}</div><div className="text-xs text-[#777]">{s.email}</div></div>
            <button type="button" onClick={() => setForm(prev => ({ ...prev, staff: (prev.staff || []).filter((_, j) => j !== i) }))} className="text-[#777] hover:text-[#C44545]" data-testid={`remove-staff-${i}`}><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-5">
        <Input type="email" value={staffEmail} onChange={e => setStaffEmail(e.target.value)} placeholder="Staff member's email" className="flex-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="staff-email-input" />
        <Button type="button" disabled={addingStaff || !staffEmail.trim()} onClick={handleAddStaff} variant="outline" className="border-[#C6A66B]/40 text-[#C6A66B]" data-testid="add-staff-button">
          {addingStaff ? '...' : 'Add'}
        </Button>
      </div>

      <div className="border-t border-[#1A1A1A] pt-4">
        <Label className="text-[#EAE7E1] text-sm font-medium">Staff Access Window (optional)</Label>
        <p className="text-xs text-[#777] mb-3">Staff can only access check-in during this window. Host always has access.</p>
        {(() => {
          const startDt = resolveEventStartDateTime({ date: form.date, time: form.time });
          const endDt = resolveEventEndDateTime({
            date: form.date,
            time: form.time,
            end_time: form.end_time,
            end_date: form.end_date,
            is_multi_day: form.is_multi_day,
          });
          const applyStart = (minutes) => {
            if (!startDt) return;
            setForm(prev => ({ ...prev, access_start_datetime: _toLocalInput(new Date(startDt.getTime() - minutes * 60000)) }));
          };
          const applyEnd = (minutes) => {
            if (!endDt) return;
            setForm(prev => ({ ...prev, access_end_datetime: _toLocalInput(new Date(endDt.getTime() + minutes * 60000)) }));
          };
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                  <Label className="text-xs text-[#AFAFAF]">Opens at</Label>
                  <div className="flex gap-1" data-testid="staff-window-start-presets">
                    {_START_PRESETS.map(p => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => applyStart(p.m)}
                        disabled={!startDt}
                        className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${!startDt ? 'border-[#1A1A1A] text-[#333] cursor-not-allowed' : 'border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:border-[#D4AF37]/40'}`}
                        data-testid={`staff-window-start-preset-${p.m}`}
                        title={!startDt ? 'Set event date and start time first' : `Open ${p.label} before start`}
                      >{p.label}</button>
                    ))}
                  </div>
                </div>
                <Input type="datetime-local" value={form.access_start_datetime || ''} onChange={e => setForm(prev => ({ ...prev, access_start_datetime: e.target.value }))} className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="access-start-input" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                  <Label className="text-xs text-[#AFAFAF]">Closes at</Label>
                  <div className="flex gap-1" data-testid="staff-window-end-presets">
                    {_END_PRESETS.map(p => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => applyEnd(p.m)}
                        disabled={!endDt}
                        className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${!endDt ? 'border-[#1A1A1A] text-[#333] cursor-not-allowed' : 'border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:border-[#D4AF37]/40'}`}
                        data-testid={`staff-window-end-preset-${p.m}`}
                        title={!endDt ? 'Set event date and end time first' : `Close ${p.label} after end`}
                      >{p.label}</button>
                    ))}
                  </div>
                </div>
                <Input type="datetime-local" value={form.access_end_datetime || ''} onChange={e => setForm(prev => ({ ...prev, access_end_datetime: e.target.value }))} className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="access-end-input" />
              </div>
            </div>
          );
        })()}
        {onSaveDefault && (() => {
          // Compute positive offsets that match host_defaults schema:
          //   pre_min  = minutes BEFORE event start
          //   post_min = minutes AFTER event end
          const startDt = resolveEventStartDateTime({ date: form.date, time: form.time });
          const endDt = resolveEventEndDateTime({
            date: form.date, time: form.time,
            end_time: form.end_time, end_date: form.end_date, is_multi_day: form.is_multi_day,
          });
          const startVal = form.access_start_datetime ? new Date(form.access_start_datetime) : null;
          const endVal = form.access_end_datetime ? new Date(form.access_end_datetime) : null;
          const preMin = startDt && startVal && !Number.isNaN(startVal.getTime())
            ? Math.round((startDt.getTime() - startVal.getTime()) / 60000) : null;
          const postMin = endDt && endVal && !Number.isNaN(endVal.getTime())
            ? Math.round((endVal.getTime() - endDt.getTime()) / 60000) : null;
          const canSave = preMin != null || postMin != null;
          const outOfRange =
            (preMin != null && (preMin < -1440 || preMin > 1440)) ||
            (postMin != null && (postMin < -1440 || postMin > 1440));
          const disabled = !canSave || outOfRange;
          return (
            <div className="mt-3 pt-3 border-t border-[#1A1A1A] flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-[#777] flex-1 min-w-0">
                {canSave && !outOfRange
                  ? `Save (${preMin != null ? (preMin >= 0 ? `-${preMin}m` : `+${-preMin}m`) : '—'} / ${postMin != null ? (postMin >= 0 ? `+${postMin}m` : `-${-postMin}m`) : '—'}) as your personal default for new events.`
                  : 'Set both open and close times to save as default.'}
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onSaveDefault({ pre_min: preMin, post_min: postMin })}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm border transition-colors ${
                  disabled
                    ? 'border-[#1A1A1A] text-[#444] cursor-not-allowed'
                    : 'border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/70'
                }`}
                data-testid="staff-window-save-default"
                title={outOfRange ? 'Offsets must be within ±24h' : 'Save as default for new events'}
              >
                <Save className="w-3 h-3" />
                Save as default
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
